import {
  ChevronsUpDown,
  LogOut,
  CheckIcon,
  MoonIcon,
  SunIcon,
  UserCircleIcon,
} from "lucide-react";
import logoInverted from "@/assets/logo-inverted.svg";
import logo from "@/assets/logo.svg";
import SeFlag from "@/assets/flags/se.svg";
import UsFlag from "@/assets/flags/us.svg";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { User } from "@/shared/client";
import useTheme from "@/shared/hooks/use-theme";
import useAuth from "@/shared/hooks/use-auth";
import { useTranslations } from "use-intl";
import { Link, useNavigate } from "react-router";
import useLanguage from "@/shared/hooks/use-language";
import { Language } from "@/pages/routing/providers/language/language.types";
import { cn } from "@/components/lib/utils";

export function NavUser({ user }: { user: User }) {
  const { isMobile } = useSidebar();
  const { isDarkMode, theme, setTheme } = useTheme();
  const { logout } = useAuth();
  const t = useTranslations();
  const tLang = useTranslations("language");
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();

  const languageOptions = [
    { key: "en-US" as Language, flag: UsFlag, name: tLang("english") },
    { key: "sv-SE" as Language, flag: SeFlag, name: tLang("swedish") },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage
                  src={user.pictureUrl || (isDarkMode ? logoInverted : logo)}
                  alt={
                    user.firstName.charAt(0) + (user.lastName?.charAt(0) || "")
                  }
                />
                <AvatarFallback className="rounded-lg">
                  {user.firstName.charAt(0) + (user.lastName?.charAt(0) || "")}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {user.firstName} {user.lastName || ""}
                </span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage
                    src={user.pictureUrl}
                    alt={
                      user.firstName.charAt(0) +
                      (user.lastName?.charAt(0) || "")
                    }
                  />
                  <AvatarFallback className="rounded-lg">
                    {user.firstName.charAt(0) +
                      (user.lastName?.charAt(0) || "")}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {user.firstName} {user.lastName || ""}
                  </span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/admin/account" className="flex items-center">
                <UserCircleIcon className="mr-2" size={16} />
                {t("sidebar.my-account")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => setTheme("light")}>
                <SunIcon className="mr-2" size={16} />
                {t("theme.light")}
                <CheckIcon
                  size={14}
                  className={cn("ml-auto", theme !== "light" && "hidden")}
                />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("dark")}>
                <MoonIcon className="mr-2" size={16} />
                {t("theme.dark")}
                <CheckIcon
                  size={14}
                  className={cn("ml-auto", theme !== "dark" && "hidden")}
                />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("system")}>
                <SunIcon className="mr-2" size={16} />
                {t("theme.system")}
                <CheckIcon
                  size={14}
                  className={cn("ml-auto", theme !== "system" && "hidden")}
                />
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {languageOptions.map((lang) => (
                <DropdownMenuItem
                  key={lang.key}
                  onClick={() => setLanguage(lang.key)}
                >
                  <img
                    src={lang.flag}
                    alt={lang.name}
                    className="w-5 h-4 mr-2 object-cover rounded-sm"
                  />
                  {lang.name}
                  <CheckIcon
                    size={14}
                    className={cn("ml-auto", language !== lang.key && "hidden")}
                  />
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2" />
              {t("userNav.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
